import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Calendar, MapPin, Clock, ChevronLeft, ChevronRight,
  ChevronDown, Plus, Users
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, 
  isSameDay, addMonths, subMonths, parseISO, isToday, isThisWeek, isFuture, isPast } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SchedulePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [myTeamIds, setMyTeamIds] = useState([]);
  const [myDivisionIds, setMyDivisionIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filterType, setFilterType] = useState('');
  const [scheduleScope, setScheduleScope] = useState('my_teams');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [showDivDropdown, setShowDivDropdown] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin' || user?.role === 'treasurer';
  // Active Parent/Admin view (written by ParentLayout into sessionStorage on
  // every toggle). handleViewSwitch always navigates after toggling, so this
  // page remounts and re-reads on render — no subscription needed.
  const activeView = typeof window !== 'undefined'
    ? (sessionStorage.getItem('pcc_active_view') || 'parent')
    : 'parent';
  const canCreateEvents =
    activeView !== 'parent'
    && ['super_admin', 'treasurer', 'league_admin', 'coach', 'team_staff', 'team_mom']
         .includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API_URL}/api/events`, { withCredentials: true }),
        axios.get(`${API_URL}/api/divisions`, { withCredentials: true })
      ];

      // Fetch parent dashboard data for "My Teams"
      if (user?.role === 'parent') {
        requests.push(
          axios.get(`${API_URL}/api/dashboard/parent`, { withCredentials: true }).catch(() => ({ data: {} }))
        );
      } else if (user?.role === 'coach' || user?.role === 'team_staff') {
        requests.push(
          axios.get(`${API_URL}/api/coach/my-teams`, { withCredentials: true }).catch(() => ({ data: [] }))
        );
      }

      const results = await Promise.all(requests);
      const allEvents = results[0].data || [];
      // Sort by date — soonest first, upcoming before past
      allEvents.sort((a, b) => {
        const aFuture = isFuture(parseISO(a.start_time));
        const bFuture = isFuture(parseISO(b.start_time));
        if (aFuture && !bFuture) return -1;
        if (!aFuture && bFuture) return 1;
        return new Date(a.start_time) - new Date(b.start_time);
      });
      setEvents(allEvents);
      setDivisions(results[1].data || []);

      // Build "My Teams" IDs
      if (user?.role === 'parent' && results[2]) {
        const dash = results[2].data;
        const teamIds = (dash.players || []).map(p => p.team_id).filter(Boolean);
        const divIds = (dash.registrations || []).map(r => r.division_id).filter(Boolean);
        setMyTeamIds(teamIds);
        setMyDivisionIds(divIds);
        // If parent has no teams, default to all
        if (teamIds.length === 0 && divIds.length === 0) {
          setScheduleScope('all');
        }
      } else if ((user?.role === 'coach' || user?.role === 'team_staff') && results[2]) {
        const teams = results[2].data || [];
        setMyTeamIds(teams.map(t => t.team_id));
        setMyDivisionIds(teams.map(t => t.division_id).filter(Boolean));
        if (teams.length === 0) setScheduleScope('all');
      } else {
        // Admin - default to all
        setScheduleScope('all');
      }
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      // Type filter
      if (filterType && event.event_type !== filterType) return false;

      // Scope filter
      if (scheduleScope === 'my_teams') {
        const matchesTeam = event.team_id && myTeamIds.includes(event.team_id);
        const matchesDiv = event.division_id && myDivisionIds.includes(event.division_id);
        if (!matchesTeam && !matchesDiv && !event.org_wide) return false;
      } else if (scheduleScope === 'all' && divisionFilter) {
        // Division sub-filter within "All Events"
        const matchesDiv = event.division_id === divisionFilter;
        // Also check team→division mapping from divisions data
        if (!matchesDiv && !event.org_wide) return false;
      }

      return true;
    });
  }, [events, filterType, scheduleScope, divisionFilter, myTeamIds, myDivisionIds]);

  const eventTypes = useMemo(() => {
    const types = new Set(events.map(e => e.event_type));
    return Array.from(types);
  }, [events]);

  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const getEventsForDay = (day) => {
    return filteredEvents.filter(event => isSameDay(parseISO(event.start_time), day));
  };

  const upcomingThisWeek = useMemo(() => {
    return filteredEvents.filter(e => {
      const d = parseISO(e.start_time);
      return isThisWeek(d) && isFuture(d) && e.status !== 'canceled';
    });
  }, [filteredEvents]);

  const getEventTypeColor = (type) => {
    const colors = {
      practice: 'bg-light-blue text-white',
      game: 'bg-red text-white',
      meeting: 'bg-navy text-white',
      equipment_day: 'bg-gold text-navy',
      orientation: 'bg-purple-600 text-white',
      fundraiser: 'bg-green-600 text-white',
      photo_day: 'bg-pink-600 text-white',
      community: 'bg-teal-600 text-white',
      scrimmage: 'bg-orange-600 text-white',
      other: 'bg-gray-600 text-white'
    };
    return colors[type] || colors.other;
  };

  const getStatusBadge = (status) => {
    if (status === 'canceled') return <Badge className="bg-red/10 text-red text-[10px]">Canceled</Badge>;
    if (status === 'updated') return <Badge className="bg-yellow-100 text-yellow-800 text-[10px]">Updated</Badge>;
    if (status === 'completed') return <Badge className="bg-gray-100 text-gray-600 text-[10px]">Completed</Badge>;
    return null;
  };

  const scopeLabel = scheduleScope === 'my_teams' ? 'My Teams' : 'All Events';

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-28 bg-navy/20 rounded-xl"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-lg"></div>)}
            </div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="schedule-page">
        {/* Header Banner */}
        <div className="rounded-xl p-6 mb-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('/field-bg-3.jpg')` }} />
          <div className="absolute inset-0 bg-navy/65" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">{scopeLabel}</p>
              <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter">Schedule</h1>
            </div>
            <div className="flex items-center space-x-2 mt-4 sm:mt-0">
              <div className="bg-white/10 rounded-lg p-1 flex">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                    viewMode === 'list' ? 'bg-white text-navy shadow' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="view-list-btn"
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                    viewMode === 'calendar' ? 'bg-white text-navy shadow' : 'text-white/70 hover:text-white'
                  }`}
                  data-testid="view-calendar-btn"
                >
                  Calendar
                </button>
              </div>
              {canCreateEvents && (
                <Button 
                  className="bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-xs" 
                  onClick={() => navigate('/admin/events/new')}
                  data-testid="add-event-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Filter Bar — clean single row */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="schedule-filters">
          {/* Scope Toggle: My Teams | All Events */}
          <div className="bg-gray-100 rounded-lg p-1 flex" data-testid="scope-toggle">
            {(myTeamIds.length > 0 || myDivisionIds.length > 0) && (
              <button
                onClick={() => { setScheduleScope('my_teams'); setDivisionFilter(''); }}
                className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                  scheduleScope === 'my_teams' ? 'bg-navy text-white shadow' : 'text-gray-600 hover:text-navy'
                }`}
                data-testid="scope-my-teams"
              >
                <Users className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                My Teams
              </button>
            )}
            <button
              onClick={() => { setScheduleScope('all'); setDivisionFilter(''); }}
              className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all duration-200 ${
                scheduleScope === 'all' ? 'bg-navy text-white shadow' : 'text-gray-600 hover:text-navy'
              }`}
              data-testid="scope-all"
            >
              All Events
            </button>
          </div>

          {/* Division Dropdown — only when "All Events" is selected */}
          {scheduleScope === 'all' && (
            <div className="relative" data-testid="division-dropdown">
              <button
                onClick={() => setShowDivDropdown(!showDivDropdown)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all duration-200 ${
                  divisionFilter ? 'bg-navy/10 border-navy/30 text-navy' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid="division-dropdown-btn"
              >
                {divisionFilter 
                  ? divisions.find(d => d.division_id === divisionFilter)?.name || 'Division'
                  : 'All Divisions'}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showDivDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowDivDropdown(false)} />
                  <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 min-w-[220px] max-h-64 overflow-y-auto" data-testid="division-dropdown-list">
                    <button
                      onClick={() => { setDivisionFilter(''); setShowDivDropdown(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        !divisionFilter ? 'font-semibold text-navy bg-navy/5' : 'text-gray-700'
                      }`}
                    >
                      All Divisions
                    </button>
                    {divisions.map(div => (
                      <button
                        key={div.division_id}
                        onClick={() => { setDivisionFilter(div.division_id); setShowDivDropdown(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                          divisionFilter === div.division_id ? 'font-semibold text-navy bg-navy/5' : 'text-gray-700'
                        }`}
                        data-testid={`div-option-${div.division_id}`}
                      >
                        {div.logo_url && (
                          <img src={div.logo_url} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                        )}
                        <span>{div.name}</span>
                        {div.age_range && <span className="text-gray-400 text-xs ml-auto">{div.age_range}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Type Dropdown */}
          {eventTypes.length > 1 && (
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-all cursor-pointer"
              data-testid="type-dropdown"
            >
              <option value="">All Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          )}

          {/* Event count */}
          <span className="text-xs text-gray-400 ml-auto">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* This Week Summary */}
        {scheduleScope === 'my_teams' && upcomingThisWeek.length > 0 && (
          <div className="mb-6 bg-navy/5 border border-navy/10 rounded-xl p-4" data-testid="this-week-summary">
            <p className="text-xs font-bold uppercase tracking-wider text-navy mb-2">This Week</p>
            <div className="flex flex-wrap gap-2">
              {upcomingThisWeek.map(e => (
                <div
                  key={e.event_id}
                  onClick={() => navigate(`/events/${e.event_id}`)}
                  className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer hover:shadow-sm transition-all"
                >
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${getEventTypeColor(e.event_type)}`}>
                    {e.event_type.replace(/_/g, ' ').slice(0, 4)}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-navy leading-tight">{e.title}</p>
                    <p className="text-[11px] text-gray-400">{format(parseISO(e.start_time), 'EEE h:mm a')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'list' ? (
          <div className="space-y-3">
            {filteredEvents.length > 0 ? (
              filteredEvents.map(event => {
                const eventDate = parseISO(event.start_time);
                const isUpcoming = isFuture(eventDate);
                return (
                  <Card 
                    key={event.event_id}
                    className={`hover:border-navy/30 transition-all duration-200 cursor-pointer ${
                      event.status === 'canceled' ? 'opacity-50' : !isUpcoming ? 'opacity-70' : ''
                    }`}
                    onClick={() => navigate(`/events/${event.event_id}`)}
                    data-testid={`event-card-${event.event_id}`}
                  >
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex items-start space-x-4">
                        {/* Date block */}
                        <div className={`text-center min-w-[52px] p-2 rounded-lg ${
                          isToday(eventDate) ? 'bg-red/10 border border-red/20' :
                          isUpcoming ? 'bg-navy/5' : 'bg-gray-50'
                        }`}>
                          <p className="text-[10px] font-bold text-gray-500 uppercase">
                            {format(eventDate, 'EEE')}
                          </p>
                          <p className={`text-2xl font-black leading-tight ${
                            isToday(eventDate) ? 'text-red' : 'text-navy'
                          }`}>
                            {format(eventDate, 'd')}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase">
                            {format(eventDate, 'MMM')}
                          </p>
                        </div>

                        {/* Event details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center flex-wrap gap-1.5 mb-1">
                            <Badge className={`${getEventTypeColor(event.event_type)} text-[10px]`}>
                              {event.event_type.replace(/_/g, ' ')}
                            </Badge>
                            {getStatusBadge(event.status)}
                            {event.org_wide && (
                              <Badge variant="outline" className="text-[10px]">League-Wide</Badge>
                            )}
                            {isToday(eventDate) && event.status !== 'canceled' && (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">Today</Badge>
                            )}
                          </div>
                          <h3 className={`font-bold text-navy ${
                            event.status === 'canceled' ? 'line-through' : ''
                          }`}>
                            {event.title}
                          </h3>
                          {event.team_name && (
                            <p className="text-xs text-gray-500 mt-0.5">{event.team_name}</p>
                          )}
                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                            <span className="flex items-center">
                              <Clock className="w-3.5 h-3.5 mr-1" />
                              {format(eventDate, 'h:mm a')}
                              {event.end_time && ` – ${format(parseISO(event.end_time), 'h:mm a')}`}
                            </span>
                            {event.location_name && (
                              <span className="flex items-center">
                                <MapPin className="w-3.5 h-3.5 mr-1" />
                                {event.location_name}
                              </span>
                            )}
                          </div>
                          {event.status === 'canceled' && event.cancellation_reason && (
                            <p className="mt-1.5 text-xs text-red bg-red/5 px-2 py-1 rounded">
                              {event.cancellation_reason}
                            </p>
                          )}
                        </div>

                        {/* RSVP summary */}
                        {event.rsvp_summary && isUpcoming && (
                          <div className="hidden sm:flex items-center space-x-3 text-center flex-shrink-0">
                            <div>
                              <p className="text-lg font-bold text-green-600">{event.rsvp_summary.attending}</p>
                              <p className="text-[10px] text-gray-400">Going</p>
                            </div>
                            <div>
                              <p className="text-lg font-bold text-red">{event.rsvp_summary.absent}</p>
                              <p className="text-[10px] text-gray-400">Out</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 bg-navy/5 rounded-full flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-navy/20" />
                </div>
                <h3 className="font-display text-lg font-bold text-navy mb-1">
                  {scheduleScope === 'my_teams' ? 'No events for your teams yet' : 'No events found'}
                </h3>
                <p className="text-sm text-gray-400 max-w-xs mx-auto">
                  {scheduleScope === 'my_teams' 
                    ? 'Your coaches will post the schedule here once the season kicks off.'
                    : 'Try changing the filters above or check back later.'}
                </p>
                {scheduleScope === 'my_teams' && (
                  <Button
                    variant="outline"
                    className="mt-4 text-xs font-bold uppercase tracking-wider"
                    onClick={() => { setScheduleScope('all'); setDivisionFilter(''); }}
                    data-testid="switch-to-all"
                  >
                    View All Events
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Calendar View */
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-navy">
                {format(currentMonth, 'MMMM yyyy')}
              </CardTitle>
              <div className="flex items-center space-x-2">
                <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setCurrentMonth(new Date())} className="px-3 py-1 text-sm font-medium hover:bg-gray-100 rounded-lg">
                  Today
                </button>
                <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: calendarDays[0]?.getDay() || 0 }).map((_, idx) => (
                  <div key={`empty-${idx}`} className="h-24 bg-gray-50 rounded-lg"></div>
                ))}
                {calendarDays.map(day => {
                  const dayEvents = getEventsForDay(day);
                  const isCurrentDay = isToday(day);
                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-24 p-1 border rounded-lg ${
                        isCurrentDay ? 'border-navy bg-navy/5' : 'border-gray-100'
                      }`}
                    >
                      <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-navy' : 'text-gray-700'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1">
                        {dayEvents.slice(0, 3).map(event => (
                          <div
                            key={event.event_id}
                            onClick={() => navigate(`/events/${event.event_id}`)}
                            className={`text-xs p-1 rounded truncate cursor-pointer ${
                              getEventTypeColor(event.event_type)
                            } ${event.status === 'canceled' ? 'opacity-50 line-through' : ''}`}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 pl-1">+{dayEvents.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ParentLayout>
  );
};

export default SchedulePage;
